import { Empty } from 'antd';
import { useEffect } from 'react';
import { Pagination, SortableHeader, Table, Top } from 'components/Table';
import Checkbox from 'components/checkbox';
import { ContentTitle } from 'pages/Playlists/PlaylistDetail/styles';
import DeadlineFilterDropdown from './DeadlineFilterDropdown';
import Spinner from 'components/loadings/Spinner';
import Icon from 'components/icons';
import Tooltip from 'components/tooltip';
import SortTable from 'components/sortTable';
import UsersRow from './UsersRow';
import { isMobile } from 'react-device-detect';
import { Container, DeadlineFilterButton, HeaderRow } from './styles';
import { ContainerDesktop } from 'components/usersTable/styles';
import { IUserTableParams } from './types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getColumnDirection } from 'utils/sorting';
import { UNSORTED } from 'types/sorting';

// Non-sortable column header with an optional info tooltip. Used for columns
// the backend can't sort yet (items_started / trackable_items_finished are
// gated behind PLAYLIST_ITEMS_SORT_ENABLED — see DEV-5357) and as the
// fallback when the sorting flag is off. `extra` renders trailing controls
// (e.g. the deadline filter) so they survive the flag-off fallback.
const PlainHeader = ({
  label,
  tooltip,
  extra,
}: {
  label: string;
  tooltip?: string;
  extra?: React.ReactNode;
}): JSX.Element => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
    {label}
    {tooltip && (
      <Tooltip placement="top" title={tooltip} overlayInnerStyle={{ textAlign: 'center' }}>
        <span style={{ display: 'inline-flex' }}>
          <Icon icon="Info" fill="var(--lilac)" size="small" />
        </span>
      </Tooltip>
    )}
    {extra}
  </span>
);

const UsersTable: React.FC<IUserTableParams> = ({
  lock,
  playlist,
  playlistUsers,
  fetching,
  removeUser,
  isSonar,
  isAccountPlaylist,
  disableManualAssignments,
  pagination,
  onPaginationChange,
  selectionEnabled,
  selectedDeadlineFilters = [],
  sortState = UNSORTED,
  selectedUserIds = [],
  allUsersSelected,
  hasPartiallySelectedUsers,
  onToggleSelectAllUsers,
  onToggleUserSelection,
  hasBulkActionBar,
  onEditDeadline,
  onDeadlineFiltersChange,
  onSortToggle,
  onSortSelect,
}) => {
  const { playlistDeadlinesMvp, dashboardUsersSorting, playlistItemsSortEnabled } =
    useFlags() || {};
  const showDeadlineColumn = Boolean(playlistDeadlinesMvp && !isSonar);
  const showSelectionColumn = Boolean(selectionEnabled && showDeadlineColumn && !isMobile);
  const isLoadingWithoutUsers = fetching && playlistUsers.length === 0;
  const showMobileSort = Boolean(isMobile && dashboardUsersSorting && !isSonar);
  const showItemsSort = Boolean(dashboardUsersSorting && playlistItemsSortEnabled);

  // The header grid always uses the desktop template; ContainerDesktop hides
  // the columns below screenMd via CSS, so the grid never collapses into an
  // overlapping layout on narrow widths.
  const headerColumns = showSelectionColumn
    ? '40px 6fr 3fr 3fr 3fr 3fr 3fr'
    : showDeadlineColumn
    ? '6fr 3fr 3fr 3fr 3fr 3fr'
    : '6fr 3fr 3fr 3fr 3fr';

  // Mobile sort dropdown options, mirroring the sortable desktop columns.
  const mobileSortItems = [
    {
      label: 'Clear sort',
      value: '',
      selected: !sortState.columnKey,
    },
    {
      label: 'User ASC',
      value: 'pl-user',
      selected: sortState.columnKey === 'pl-user' && sortState.direction === 'asc',
    },
    {
      label: 'User DESC',
      value: '-pl-user',
      selected: sortState.columnKey === 'pl-user' && sortState.direction === 'desc',
    },
    ...(showItemsSort
      ? [
          {
            label: 'Items completed ASC',
            value: 'pl-items-completed',
            selected: sortState.columnKey === 'pl-items-completed' && sortState.direction === 'asc',
          },
          {
            label: 'Items completed DESC',
            value: '-pl-items-completed',
            selected:
              sortState.columnKey === 'pl-items-completed' && sortState.direction === 'desc',
          },
          {
            label: 'Items started ASC',
            value: 'pl-items-started',
            selected: sortState.columnKey === 'pl-items-started' && sortState.direction === 'asc',
          },
          {
            label: 'Items started DESC',
            value: '-pl-items-started',
            selected: sortState.columnKey === 'pl-items-started' && sortState.direction === 'desc',
          },
        ]
      : []),
    {
      label: 'Last activity date ASC',
      value: 'pl-last-activity',
      selected: sortState.columnKey === 'pl-last-activity' && sortState.direction === 'asc',
    },
    {
      label: 'Last activity date DESC',
      value: '-pl-last-activity',
      selected: sortState.columnKey === 'pl-last-activity' && sortState.direction === 'desc',
    },
    ...(showDeadlineColumn
      ? [
          {
            label: 'Deadline ASC',
            value: 'pl-deadline',
            selected: sortState.columnKey === 'pl-deadline' && sortState.direction === 'asc',
          },
          {
            label: 'Deadline DESC',
            value: '-pl-deadline',
            selected: sortState.columnKey === 'pl-deadline' && sortState.direction === 'desc',
          },
        ]
      : []),
  ];

  const handleMobileSortSelect = ({ value }: { value: string }) => {
    onSortSelect?.(value);
  };

  // When the sorting flag is off, drop any persisted generic sort so a stale
  // session value can't keep ordering the list.
  useEffect(() => {
    if (!dashboardUsersSorting && sortState.columnKey) {
      onSortSelect?.('');
    }
  }, [dashboardUsersSorting]);

  // When items sort is disabled, clear any stale session sort on those columns.
  useEffect(() => {
    if (
      !playlistItemsSortEnabled &&
      sortState.columnKey &&
      ['pl-items-completed', 'pl-items-started'].includes(sortState.columnKey)
    ) {
      onSortSelect?.('');
    }
  }, [playlistItemsSortEnabled]);

  // When the deadline column is hidden, clear any stale session sort on it.
  useEffect(() => {
    if (!showDeadlineColumn && sortState.columnKey === 'pl-deadline') {
      onSortSelect?.('');
    }
  }, [showDeadlineColumn, sortState.columnKey, onSortSelect]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const EMPTY_LOADING_MIN_HEIGHT = isMobile ? 260 : 360;

  const hasSelectedDeadlineFilters = selectedDeadlineFilters.length > 0;
  const deadlineFilterActive = showDeadlineColumn && hasSelectedDeadlineFilters;
  const showEmptyState = !fetching && playlistUsers.length === 0;
  const emptyStateDescription = hasSelectedDeadlineFilters
    ? 'No results for the selected filters'
    : 'No data';

  // Shared by the sortable header (extra slot) and the flag-off PlainHeader
  // fallback, so the filter control is identical in both render paths.
  const deadlineFilter = (
    <DeadlineFilterDropdown
      selectedValues={selectedDeadlineFilters}
      onChange={(nextValues) => onDeadlineFiltersChange?.(nextValues)}
      renderTrigger={() => (
        <DeadlineFilterButton
          type="button"
          aria-label="Filter deadlines"
          aria-pressed={deadlineFilterActive}
          data-active={deadlineFilterActive}
          $active={deadlineFilterActive}
        >
          <Icon icon="Filter" size="small" color={deadlineFilterActive ? '#FFFFFF' : '#A793F6'} />
        </DeadlineFilterButton>
      )}
    />
  );

  return (
    <div
      style={{
        position: 'relative',
        minHeight: isLoadingWithoutUsers ? EMPTY_LOADING_MIN_HEIGHT : undefined,
      }}
    >
      <Spinner active={fetching} top={isLoadingWithoutUsers ? '58%' : '35%'} zIndex="1000" />
      <Container lock={lock}>
        <Table>
          <HeaderRow
            columns={headerColumns}
            transparent={isMobile}
            topSelect={isMobile}
            noCtaHeader={isSonar}
            top
            flushTopPadding={Boolean(hasBulkActionBar)}
          >
            {(showSelectionColumn || showMobileSort) && (
              <Top topSelect={isMobile}>
                {showSelectionColumn && (
                  <Checkbox
                    aria-label="Select all assigned users"
                    checked={Boolean(allUsersSelected)}
                    indeterminate={Boolean(hasPartiallySelectedUsers)}
                    controlled
                    onChange={() => onToggleSelectAllUsers?.()}
                  />
                )}
                {showMobileSort && (
                  <SortTable
                    title="Sort by"
                    name="ordering"
                    position="left"
                    items={mobileSortItems}
                    onSelect={handleMobileSortSelect}
                  />
                )}
              </Top>
            )}

            <ContainerDesktop>
              {!isSonar ? (
                <Top>
                  {dashboardUsersSorting ? (
                    <SortableHeader
                      label="User"
                      columnKey="pl-user"
                      direction={getColumnDirection(sortState, 'pl-user')}
                      onToggle={onSortToggle ?? noop}
                    />
                  ) : (
                    <PlainHeader label="User" />
                  )}
                </Top>
              ) : (
                <ContentTitle>Playlist Progress</ContentTitle>
              )}
            </ContainerDesktop>

            <ContainerDesktop>
              <Top>
                {showItemsSort ? (
                  <SortableHeader
                    label="Items completed"
                    columnKey="pl-items-completed"
                    direction={getColumnDirection(sortState, 'pl-items-completed')}
                    onToggle={onSortToggle ?? noop}
                    tooltip="The completion of all trackable items"
                  />
                ) : (
                  <PlainHeader
                    label="Items completed"
                    tooltip="The completion of all trackable items"
                  />
                )}
              </Top>
            </ContainerDesktop>
            <ContainerDesktop>
              <Top>
                {showItemsSort ? (
                  <SortableHeader
                    label="Items started"
                    columnKey="pl-items-started"
                    direction={getColumnDirection(sortState, 'pl-items-started')}
                    onToggle={onSortToggle ?? noop}
                    tooltip="The count of all items that have been started"
                  />
                ) : (
                  <PlainHeader
                    label="Items started"
                    tooltip="The count of all items that have been started"
                  />
                )}
              </Top>
            </ContainerDesktop>
            <ContainerDesktop>
              <Top>
                {dashboardUsersSorting ? (
                  <SortableHeader
                    label="Last activity date"
                    columnKey="pl-last-activity"
                    direction={getColumnDirection(sortState, 'pl-last-activity')}
                    onToggle={onSortToggle ?? noop}
                    tooltip="The most recent date the learner made progress on this playlist after assignment"
                  />
                ) : (
                  <PlainHeader
                    label="Last activity date"
                    tooltip="The most recent date the learner made progress on this playlist after assignment"
                  />
                )}
              </Top>
            </ContainerDesktop>

            {showDeadlineColumn && (
              <ContainerDesktop>
                <Top>
                  {dashboardUsersSorting ? (
                    <SortableHeader
                      label="Deadline"
                      columnKey="pl-deadline"
                      direction={getColumnDirection(sortState, 'pl-deadline')}
                      onToggle={onSortToggle ?? noop}
                      extra={deadlineFilter}
                    />
                  ) : (
                    <PlainHeader label="Deadline" extra={deadlineFilter} />
                  )}
                </Top>
              </ContainerDesktop>
            )}
          </HeaderRow>

          {playlistUsers.map((playlistUser) => (
            <UsersRow
              key={playlistUser.user.id}
              playlist={playlist}
              item={playlistUser}
              isAccountPlaylist={isAccountPlaylist}
              removeUser={removeUser}
              isSonar={isSonar}
              disableManualAssignments={disableManualAssignments}
              selectionEnabled={showSelectionColumn}
              isSelected={selectedUserIds.includes(playlistUser.user.id)}
              onToggleUserSelection={onToggleUserSelection}
              onEditDeadline={onEditDeadline}
            />
          ))}

          {isLoadingWithoutUsers && <div style={{ minHeight: EMPTY_LOADING_MIN_HEIGHT }} />}

          {showEmptyState && (
            <div style={{ padding: '32px 0 24px' }}>
              <Empty description={emptyStateDescription} />
            </div>
          )}
        </Table>
        {pagination && onPaginationChange && (
          <Pagination
            className="w-100"
            count={pagination.count}
            page={pagination.page}
            page_size={pagination.page_size}
            onPagesClick={onPaginationChange}
          />
        )}
      </Container>
    </div>
  );
};

export default UsersTable;
